// src/MarketApp.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Routes, Route, Link, useNavigate, useParams } from "react-router-dom";

import CrossListKit from "./components/CrossListKit";
import { loadListings, saveListings, upsertListing, getListingById } from "./lib/listingStore";

const CATEGORIES = [
  "Volunteer / Internship",
  "Community Clean-up",
  "Donations",
  "Products",
  "Services",
  "Events",
  "Real Estate",
  "Vehicles",
  "Electronics",
  "Medical / Health",
  "Education",
  "Other",
];

const mockListings = [
  {
    id: 1,
    title: "Volunteer / Community Clean-up",
    location: "Miami, FL",
    price: "Free",
    category: "Volunteer / Internship",
  },
  {
    id: 2,
    title: "Donated Car — Clean Title",
    location: "Kendall, FL",
    price: "$0 (Donated)",
    category: "Donations",
  },
];

/* =======================
   ELECTRIC IMPULSE OVERLAY
   - Many paths
   - Multiple impulses per path
   - Different sequences (staggered + varied duration)
   - 1.5x slower than previous version
======================= */
function ElectricImpulseOverlay({ reduceMotion }) {
  const disable = reduceMotion;
  if (disable) return null;

  // 1.5x slower multiplier
  const SLOW = 1.5;

  const PATHS = useMemo(
    () => [
      { id: "p1", d: "M 60 90  L 575 90  L 650 210 L 1000 210", stroke: "rgba(56,189,248,0.14)", width: 3 },
      { id: "p2", d: "M 0 120  L 420 120 L 520 160 L 1000 160", stroke: "rgba(56,189,248,0.10)", width: 3 },
      { id: "p3", d: "M 0 165  L 460 165 L 560 200 L 1000 200", stroke: "rgba(16,185,129,0.09)", width: 3 },
      { id: "p4", d: "M 0 210  L 430 210 L 520 240 L 1000 240", stroke: "rgba(56,189,248,0.09)", width: 3 },
      { id: "p5", d: "M 0 260  L 520 260 L 610 220 L 1000 220", stroke: "rgba(16,185,129,0.08)", width: 3 },
      { id: "p6", d: "M 0 310  L 520 310 L 650 280 L 1000 280", stroke: "rgba(56,189,248,0.08)", width: 3 },
      { id: "p7", d: "M 0 350  L 360 350 L 470 320 L 1000 320", stroke: "rgba(16,185,129,0.075)", width: 3 },
      { id: "p8", d: "M 0 395  L 470 395 L 600 430 L 1000 430", stroke: "rgba(56,189,248,0.07)", width: 3 },
      { id: "p9", d: "M 0 455  L 520 455 L 640 470 L 1000 470", stroke: "rgba(16,185,129,0.065)", width: 3 },
    ],
    []
  );

  // Base sequences, multiplied by SLOW to be 1.5x slower
  const SEQUENCES = useMemo(
    () => [
      [
        { dur: 5.2 * SLOW, begin: 0.0 * SLOW, r: 10, grad: "impulseBlue" },
        { dur: 6.6 * SLOW, begin: 1.4 * SLOW, r: 9, grad: "impulseBlue" },
        { dur: 9.6 * SLOW, begin: 3.1 * SLOW, r: 9, grad: "impulseGreen" },
      ],
      [
        { dur: 8.4 * SLOW, begin: 0.8 * SLOW, r: 9, grad: "impulseGreen" },
        { dur: 6.2 * SLOW, begin: 3.0 * SLOW, r: 9, grad: "impulseBlue" },
      ],
      [
        { dur: 10.2 * SLOW, begin: 2.2 * SLOW, r: 10, grad: "impulseBlue" },
        { dur: 5.8 * SLOW, begin: 0.6 * SLOW, r: 8, grad: "impulseGreen" },
      ],
      [
        { dur: 7.6 * SLOW, begin: 0.3 * SLOW, r: 9, grad: "impulseBlue" },
        { dur: 7.6 * SLOW, begin: 4.1 * SLOW, r: 9, grad: "impulseGreen" },
      ],
      [{ dur: 12.0 * SLOW, begin: 1.6 * SLOW, r: 10, grad: "impulseBlue" }],
    ],
    []
  );

  const pickSequence = (idx) => SEQUENCES[idx % SEQUENCES.length];

  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute inset-0"
      viewBox="0 0 1000 500"
      preserveAspectRatio="none"
      style={{ mixBlendMode: "screen", opacity: 0.95 }}
    >
      <defs>
        <filter id="glow">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        <radialGradient id="impulseBlue" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.95)" />
          <stop offset="35%" stopColor="rgba(56,189,248,0.85)" />
          <stop offset="100%" stopColor="rgba(56,189,248,0)" />
        </radialGradient>

        <radialGradient id="impulseGreen" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.75)" />
          <stop offset="40%" stopColor="rgba(16,185,129,0.70)" />
          <stop offset="100%" stopColor="rgba(16,185,129,0)" />
        </radialGradient>
      </defs>

      {/* Faint helper strokes */}
      {PATHS.map((p) => (
        <path
          key={p.id}
          id={p.id}
          d={p.d}
          fill="none"
          stroke={p.stroke}
          strokeWidth={p.width}
          strokeLinecap="round"
          filter="url(#glow)"
        />
      ))}

      {/* Impulses */}
      {PATHS.map((p, idx) => {
        const seq = pickSequence(idx);
        return (
          <g key={`imp-${p.id}`} filter="url(#glow)">
            {seq.map((s, k) => (
              <g key={`${p.id}-${k}`}>
                <circle r={s.r} fill={`url(#${s.grad})`}>
                  <animateMotion
                    dur={`${s.dur}s`}
                    begin={`${s.begin}s`}
                    repeatCount="indefinite"
                    keySplines="0.4 0 0.2 1"
                    calcMode="spline"
                  >
                    <mpath href={`#${p.id}`} />
                  </animateMotion>

                  <animate
                    attributeName="r"
                    values={`${Math.max(6, s.r - 3)};${s.r + 3};${Math.max(6, s.r - 3)}`}
                    dur={`${0.75 * SLOW}s`}
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    values="0.45;1;0.45"
                    dur={`${0.75 * SLOW}s`}
                    repeatCount="indefinite"
                  />
                </circle>

                {/* Tail spark */}
                <circle r="4" fill="rgba(255,255,255,0.55)">
                  <animateMotion
                    dur={`${s.dur}s`}
                    begin={`${s.begin + 0.18 * SLOW}s`}
                    repeatCount="indefinite"
                  >
                    <mpath href={`#${p.id}`} />
                  </animateMotion>
                  <animate
                    attributeName="opacity"
                    values="0;0.9;0"
                    dur={`${0.45 * SLOW}s`}
                    repeatCount="indefinite"
                  />
                </circle>
              </g>
            ))}
          </g>
        );
      })}
    </svg>
  );
}

export default function MarketApp() {
  const [active, setActive] = useState("listings");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [myListings, setMyListings] = useState(() => loadListings());
  const reduceMotion = useReducedMotion();
  const navigate = useNavigate();

  // Persist listings
  useEffect(() => {
    saveListings(myListings);
  }, [myListings]);

  // ✅ HOME button target:
  // - On market subdomain (market.wow5050.org): go to main landing https://wow5050.org/
  // - On localhost: go to "/"
  const homeHref = useCallback(() => {
    const host = (window.location.hostname || "").toLowerCase();
    const isLocal = host === "localhost" || host === "127.0.0.1";
    if (isLocal) return "/";

    const isMarketHost = host === "market.wow5050.org" || host.startsWith("market.");
    if (isMarketHost) return "https://wow5050.org/";

    return "https://wow5050.org/";
  }, []);

  const goHome = useCallback(() => {
    const href = homeHref();
    if (href === "/") navigate("/");
    else window.location.href = href;
  }, [homeHref, navigate]);

  const [form, setForm] = useState({
  title: "",
  price: "",
  category: "",
  condition: "New",
  location: "",
  wowCheckoutUrl: "",
  contactMethod: "Message via WOW5050",
  pickupNotes: "",
  description: "",
  images: [],
  offPlatformDisclaimer: true,
});

// Cross-list preview helpers (works even before you submit)
const draftIdRef = useRef(
  typeof crypto !== "undefined" && crypto.randomUUID
    ? `draft-${crypto.randomUUID()}`
    : `draft-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
);

const draftListingForCrossList = useMemo(() => {
  const id = draftIdRef.current;
  const listingUrl = `${window.location.origin}/listing/${id}`;

  const photos = (form.images || []).map((img, idx) => ({
    id: `${id}-${idx}`,
    dataUrl: img?.dataUrl || img,
  }));

  return {
    id,
    title: form.title || "",
    price: form.price || "",
    category: form.category || "",
    condition: form.condition || "New",
    description: form.description || "",
    location: form.location || "",
    wowCheckoutUrl: form.wowCheckoutUrl || "",
    contactMethod: form.contactMethod || "Message via WOW5050",
    pickupNotes: form.pickupNotes || "",
    offPlatformDisclaimer: !!form.offPlatformDisclaimer,
    photos,
    listingUrl,
  };
}, [form]);


  const CND = ["New", "Like New", "Good", "Fair", "Used", "For parts"];

  const filteredListings =
    selectedCategory === "All" || !selectedCategory
      ? mockListings
      : mockListings.filter((l) => l.category === selectedCategory);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? !!checked : value,
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    Promise.all(
      files.slice(0, 8).map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () =>
              resolve({
                name: file.name,
                type: file.type,
                dataUrl: reader.result,
              });
            reader.onerror = () =>
              resolve({ name: file.name, type: file.type, dataUrl: null });
            reader.readAsDataURL(file);
          })
      )
    ).then((imgs) => {
      setForm((prev) => ({
        ...prev,
        images: [...(prev.images || []), ...imgs].slice(0, 8),
      }));
    });
  };

  const createId = () => {
    try {
      return crypto.randomUUID();
    } catch {
      return String(Date.now());
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!form.title.trim()) return alert("Please add a title.");
    if (!form.price.trim()) return alert("Please add a price.");
    if (!form.category.trim()) return alert("Please select a category.");
    if (!form.location.trim()) return alert("Please add a location.");
    if (!form.wowCheckoutUrl.trim())
      return alert("Please add your WOW5050 checkout link (10% fee is taken there).");

    const id = createId();
    const listingUrl = `${window.location.origin}/listing/${id}`;

    const listing = {
      id,
      title: form.title.trim(),
      price: form.price.trim(),
      category: form.category,
      condition: form.condition,
      description: form.description.trim(),
      location: form.location.trim(),
      contactMethod: form.contactMethod.trim(),
      wowCheckoutUrl: form.wowCheckoutUrl.trim(),
      pickupNotes: form.pickupNotes.trim(),
      offPlatformDisclaimer: !!form.offPlatformDisclaimer,
      photos: (form.images || []).filter((x) => x?.dataUrl),
      listingUrl,
      createdAt: new Date().toISOString(),
      crosslist: {
        craigslist: { status: "Draft", postedUrl: "", lastOpenedAt: null },
        offerup: { status: "Draft", postedUrl: "", lastOpenedAt: null },
        facebook: { status: "Draft", postedUrl: "", lastOpenedAt: null },
      },
    };

    setMyListings((prev) => upsertListing(prev, listing));

    setForm({
      title: "",
      price: "",
      category: "",
      condition: "New",
      description: "",
      location: "",
      contactMethod: "Message via WOW5050",
      wowCheckoutUrl: "",
      pickupNotes: "",
      offPlatformDisclaimer: true,
      images: [],
    });

    setSidebarOpen(false);
    navigate(`/listing/${id}`);
  };

  const updateCrosslist = useCallback(
    (id, platform, patch) => {
      setMyListings((prev) => {
        const listing = getListingById(prev, id);
        if (!listing) return prev;

        const next = {
          ...listing,
          crosslist: {
            ...(listing.crosslist || {}),
            [platform]: {
              ...(listing.crosslist?.[platform] || {}),
              ...patch,
            },
          },
        };

        return upsertListing(prev, next);
      });
    },
    [setMyListings]
  );

  function ListingDetail() {
    const { id } = useParams();
    const listing = useMemo(
      () => getListingById(myListings, id),
      [myListings, id]
    );

    const [targets, setTargets] = useState(["facebook", "offerup", "craigslist"]);

    if (!listing) {
      return (
        <div className="max-w-2xl">
          <h2 className="text-2xl font-semibold text-emerald-300 mb-2">
            Listing not found
          </h2>
          <button
            className="mt-3 inline-flex items-center px-3 py-2 rounded-xl border border-slate-700 hover:border-emerald-400 hover:text-emerald-200 transition"
            onClick={() => navigate("/")}
          >
            ← Back to marketplace
          </button>
        </div>
      );
    }

    return (
      <div className="max-w-5xl">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-emerald-300">
              {listing.title}
            </h2>
            <p className="text-sm text-slate-300">
              {listing.price} • {listing.category} • {listing.condition} •{" "}
              {listing.location}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              className="inline-flex items-center px-3 py-2 rounded-xl border border-slate-700 hover:border-emerald-400 hover:text-emerald-200 transition"
              onClick={() => navigate("/")}
            >
              ← Marketplace
            </button>
            <a
              href={listing.wowCheckoutUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-2 rounded-xl bg-emerald-500 text-slate-900 font-semibold hover:bg-emerald-400 transition"
            >
              Open WOW Checkout
            </a>
          </div>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-5">
          <div className="lg:col-span-2 rounded-2xl border border-slate-700 bg-slate-900/60 p-5 backdrop-blur">
            <h3 className="text-sm font-semibold text-sky-300">Details</h3>
            <div className="mt-3 text-sm text-slate-200 space-y-2">
              <div>
                <div className="text-xs uppercase text-slate-400">Description</div>
                <div className="mt-1 whitespace-pre-wrap">{listing.description || "—"}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-slate-400">Pickup/Meetup</div>
                <div className="mt-1">{listing.pickupNotes || "Arrange after checkout"}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-slate-400">Contact</div>
                <div className="mt-1">{listing.contactMethod || "Message via WOW5050"}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-slate-400">WOW Listing URL</div>
                <div className="mt-1 break-all">{listing.listingUrl}</div>
              </div>
            </div>

            {listing.photos?.length ? (
              <div className="mt-4">
                <div className="text-xs uppercase text-slate-400 mb-2">
                  Photos
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {listing.photos.slice(0, 6).map((p, i) => (
                    <a
                      key={i}
                      href={p.dataUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-lg overflow-hidden border border-slate-800"
                      title="Open image"
                    >
                      <img
                        src={p.dataUrl}
                        alt={p.name || `photo-${i + 1}`}
                        className="h-24 w-full object-cover"
                        loading="lazy"
                      />
                    </a>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className="lg:col-span-3">
            <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-5 backdrop-blur">
              <h3 className="text-lg font-semibold text-emerald-300">
                Cross-List Targets
              </h3>
              <p className="text-sm text-slate-300">
                Select platforms, then use the kit below. Step A — open • Step B — paste • Step C — mark done.
              </p>

              <div className="mt-3 flex flex-wrap gap-3 text-sm">
                {[
                  ["facebook", "Facebook Marketplace"],
                  ["offerup", "OfferUp"],
                  ["craigslist", "Craigslist"],
                ].map(([k, label]) => (
                  <label
                    key={k}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-700 px-3 py-2 hover:border-emerald-400/70 transition cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={targets.includes(k)}
                      onChange={(e) => {
                        setTargets((prev) => {
                          const has = prev.includes(k);
                          if (e.target.checked && !has) return [...prev, k];
                          if (!e.target.checked && has)
                            return prev.filter((x) => x !== k);
                          return prev;
                        });
                      }}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <CrossListKit
              listing={listing}
              selectedPlatforms={targets}
              onOpenPlatform={(platform) =>
                updateCrosslist(listing.id, platform, {
                  status: "Opened",
                  lastOpenedAt: new Date().toISOString(),
                })
              }
              onStatusChange={(platform, status) =>
                updateCrosslist(listing.id, platform, { status })
              }
              onPostedUrlChange={(platform, postedUrl) =>
                updateCrosslist(listing.id, platform, { postedUrl })
              }
            />
          </div>
        </div>
      </div>
    );
  }

  function Home() {
    return (
      <>
        {active === "listings" && (
          <>
            <div className="flex items-end justify-between gap-4 mb-6">
              <div>
                <h2 className="text-2xl font-semibold text-emerald-300">
                  Marketplace Listings
                </h2>
                <p className="text-sm text-slate-300">
                  {selectedCategory === "All"
                    ? "Showing all categories."
                    : `Showing: ${selectedCategory}`}
                </p>
              </div>

              <div className="text-xs text-slate-300">
                {filteredListings.length} result
                {filteredListings.length === 1 ? "" : "s"}
              </div>
            </div>

            {myListings?.length ? (
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-sky-300 mb-3">
                  Your Listings
                </h3>
                <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {myListings.slice(0, 12).map((item) => (
                    <motion.div
                      key={item.id}
                      className="rounded-2xl border border-slate-700 bg-slate-900/60 p-5 backdrop-blur"
                      whileHover={{ y: -3, scale: 1.01 }}
                      transition={{ type: "spring", stiffness: 260, damping: 18 }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="text-lg font-semibold text-emerald-200">
                          {item.title}
                        </h3>
                        <span className="text-[11px] px-2 py-1 rounded-full border border-slate-600/70 text-slate-200">
                          {item.category}
                        </span>
                      </div>
                      <p className="text-sm text-slate-300 mt-2">
                        {item.location} • {item.condition}
                      </p>
                      <div className="mt-4 flex items-center justify-between">
                        <div className="text-emerald-300 font-semibold">
                          {item.price}
                        </div>
                        <Link
                          to={`/listing/${item.id}`}
                          className="text-sm px-3 py-2 rounded-xl border border-slate-700 hover:border-emerald-400 hover:text-emerald-200 transition"
                        >
                          Open
                        </Link>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {filteredListings.map((item) => (
                <motion.div
                  key={item.id}
                  className="rounded-2xl border border-slate-700 bg-slate-900/60 p-5 backdrop-blur"
                  whileHover={{ y: -4, scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 260, damping: 18 }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-lg font-semibold text-sky-300">
                      {item.title}
                    </h3>
                    <span className="text-[11px] px-2 py-1 rounded-full border border-slate-600/70 text-slate-200">
                      {item.category}
                    </span>
                  </div>

                  <p className="text-sm text-slate-300 mt-2">
                    {item.location}
                  </p>

                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-emerald-300 font-semibold">
                      {item.price}
                    </div>
                    <button
                      className="text-sm px-3 py-2 rounded-xl border border-slate-700 hover:border-emerald-400 hover:text-emerald-200 transition"
                      onClick={() => alert("Demo listing (mock). Your real listings appear above.")}
                    >
                      View
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        )}

        {active === "publish" && (
          <div className="max-w-2xl">
            <h2 className="text-2xl font-semibold text-emerald-300 mb-2">
              Publish a Listing
            </h2>
            <p className="text-sm text-slate-300 mb-6">
              One form = your single source of truth. Then cross-list with one-click helpers.
            </p>

            <form
              onSubmit={handleSubmit}
              className="space-y-4 bg-slate-900/60 p-6 rounded-2xl border border-slate-700 backdrop-blur"
            >
              <input
                name="title"
                placeholder="Title"
                value={form.title}
                onChange={handleChange}
                className="w-full rounded-lg bg-slate-800/80 border border-slate-700 p-3 outline-none focus:ring-2 focus:ring-emerald-500/40"
                required
              />

              <div className="grid gap-3 sm:grid-cols-2">
                <input
                  name="price"
                  placeholder="Price (Free, $0, $100, etc.)"
                  value={form.price}
                  onChange={handleChange}
                  className="w-full rounded-lg bg-slate-800/80 border border-slate-700 p-3 outline-none focus:ring-2 focus:ring-emerald-500/40"
                  required
                />

                <select
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  className="w-full rounded-lg bg-slate-800/80 border border-slate-700 p-3 outline-none focus:ring-2 focus:ring-emerald-500/40"
                  required
                >
                  <option value="">Select Category</option>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <select
                  name="condition"
                  value={form.condition}
                  onChange={handleChange}
                  className="w-full rounded-lg bg-slate-800/80 border border-slate-700 p-3 outline-none focus:ring-2 focus:ring-emerald-500/40"
                >
                  {CND.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>

                <input
                  name="location"
                  placeholder="Location (City, State)"
                  value={form.location}
                  onChange={handleChange}
                  className="w-full rounded-lg bg-slate-800/80 border border-slate-700 p-3 outline-none focus:ring-2 focus:ring-emerald-500/40"
                  required
                />
              </div>

              <input
                name="wowCheckoutUrl"
                placeholder="WOW5050 checkout link (10% fee taken here)"
                value={form.wowCheckoutUrl}
                onChange={handleChange}
                className="w-full rounded-lg bg-slate-800/80 border border-slate-700 p-3 outline-none focus:ring-2 focus:ring-emerald-500/40"
                required
              />

              <div className="grid gap-3 sm:grid-cols-2">
                <input
                  name="contactMethod"
                  placeholder="Contact method (e.g., message via WOW5050)"
                  value={form.contactMethod}
                  onChange={handleChange}
                  className="w-full rounded-lg bg-slate-800/80 border border-slate-700 p-3 outline-none focus:ring-2 focus:ring-emerald-500/40"
                />
                <input
                  name="pickupNotes"
                  placeholder="Pickup/meetup notes (optional)"
                  value={form.pickupNotes}
                  onChange={handleChange}
                  className="w-full rounded-lg bg-slate-800/80 border border-slate-700 p-3 outline-none focus:ring-2 focus:ring-emerald-500/40"
                />
              </div>

              <textarea
                name="description"
                placeholder="Description"
                value={form.description}
                onChange={handleChange}
                rows={5}
                className="w-full rounded-lg bg-slate-800/80 border border-slate-700 p-3 outline-none focus:ring-2 focus:ring-emerald-500/40"
              />

              <label className="flex items-center gap-2 text-sm text-slate-200">
                <input
                  type="checkbox"
                  name="offPlatformDisclaimer"
                  checked={form.offPlatformDisclaimer}
                  onChange={handleChange}
                />
                Include “Do not pay off-platform” disclaimer in templates
              </label>

              <div className="space-y-2">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="w-full text-sm text-slate-200"
                />

                {form.images?.length ? (
                  <div className="grid grid-cols-4 gap-2">
                    {form.images.slice(0, 8).map((p, i) => (
                      <div
                        key={i}
                        className="rounded-lg overflow-hidden border border-slate-800 bg-slate-950/40"
                      >
                        {p.dataUrl ? (
                          <img
                            src={p.dataUrl}
                            alt={p.name || `upload-${i + 1}`}
                            className="h-20 w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="h-20 flex items-center justify-center text-xs text-slate-400">
                            (image)
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">
                    Add up to 8 photos. They’ll be available in the Cross-List kit for drag/drop.
                  </p>
                )}
              </div>

              <button
                type="submit"
                className="w-full rounded-xl bg-emerald-500 px-4 py-3 font-semibold text-slate-900 hover:bg-emerald-400 transition"
              >
                Submit Listing
              </button>
            </form>

{/* Cross-list helpers (Craigslist / OfferUp / Facebook Marketplace) */}
<div className="mt-8">
  <CrossListKit
    listing={draftListingForCrossList}
    selectedPlatforms={["craigslist", "offerup", "facebook"]}
    onOpenPlatform={() => {}}
    onStatusChange={() => {}}
    onPostedUrlChange={() => {}}
  />
</div>

          </div>
        )}
      </>
    );
  }

  return (
    <div
      className="min-h-screen text-slate-100 relative overflow-hidden"
      style={{
        backgroundImage:
          "linear-gradient(rgba(0,0,0,.74), rgba(0,0,0,.74)), url(/network-bg1.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <ElectricImpulseOverlay reduceMotion={reduceMotion} />

      <div className="relative z-10 mx-auto max-w-7xl p-4 sm:p-6 md:p-8">
        {/* Mobile topbar */}
        <div className="lg:hidden sticky top-0 z-50 flex items-center justify-between mb-4 rounded-2xl border border-slate-800/70 bg-slate-950/70 backdrop-blur p-3">
          <button
            type="button"
            className="inline-flex items-center justify-center h-10 w-10 rounded-xl border border-slate-700 hover:border-emerald-400 hover:text-emerald-200 transition"
            onClick={() => setSidebarOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            ☰
          </button>
          <div className="text-sm font-semibold text-emerald-300">
            WOW5050 Marketplace
          </div>
          <button
            type="button"
            className="text-xs px-3 py-2 rounded-xl border border-slate-700 hover:border-emerald-400 hover:text-emerald-200 transition"
            onClick={goHome}
            title="Back to WOW50/50 Landing Page"
          >
            Home
          </button>
        </div>

        <div className="flex flex-col gap-6 lg:flex-row">
          {sidebarOpen && (
          <div
            className="lg:hidden fixed inset-0 z-40 bg-black/60"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        <aside
            className={`rounded-2xl border border-slate-700 bg-slate-900/60 p-5 backdrop-blur ${sidebarOpen ? "fixed z-50 inset-y-0 left-0 w-[88%] max-w-sm overflow-y-auto" : "hidden"} lg:static lg:z-auto lg:inset-auto lg:w-80 lg:block`}
          >
            <div className="lg:hidden flex justify-end mb-2"><button type="button" onClick={() => setSidebarOpen(false)} className="px-3 py-2 rounded-lg border border-slate-600 text-slate-200">Close</button></div><div className="mb-4">
              <h1 className="text-lg font-semibold text-emerald-300">
                Marketplace
              </h1>
              <p className="text-sm text-slate-300">
                Browse listings or publish your own.
              </p>

              {/* ✅ HOME BUTTON (Landing Page) */}
              <motion.button
                onClick={goHome}
                className="mt-3 w-full inline-flex items-center justify-center rounded-xl border border-slate-700 px-4 py-2 text-sm text-slate-100 hover:border-emerald-400 hover:text-emerald-200 transition"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                title="Back to WOW50/50 Landing Page"
              >
                ⟵ Home (WOW50/50)
              </motion.button>

              <button
                type="button"
                className="lg:hidden mt-2 w-full inline-flex items-center justify-center rounded-xl border border-slate-700 px-4 py-2 text-sm text-slate-100 hover:border-emerald-400 hover:text-emerald-200 transition"
                onClick={() => setSidebarOpen(false)}
              >
                Close menu
              </button>
            </div>

            <nav className="space-y-2 text-sm">
              <button
                onClick={() => {
                  setActive("listings");
                  setSidebarOpen(false);
                  navigate("/");
                }}
                className={`block w-full text-left px-3 py-2 rounded-lg ${
                  active === "listings"
                    ? "bg-emerald-500/15 text-emerald-300"
                    : "hover:bg-slate-800/70"
                }`}
              >
                Marketplace Listings
              </button>

              <button
                onClick={() => {
                  setActive("publish");
                  setSidebarOpen(false);
                  navigate("/");
                }}
                className={`block w-full text-left px-3 py-2 rounded-lg ${
                  active === "publish"
                    ? "bg-emerald-500/15 text-emerald-300"
                    : "hover:bg-slate-800/70"
                }`}
              >
                Publish Listing
              </button>
            </nav>

            {active === "listings" && (
              <div className="pt-4 mt-4 border-t border-slate-700/70">
                <h3 className="text-xs uppercase text-slate-400 mb-2">
                  Categories
                </h3>

                <div className="max-h-[50vh] overflow-auto pr-1 space-y-1">
                  {["All", ...CATEGORIES].map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className={`block w-full text-left px-3 py-2 rounded-lg text-sm ${
                        selectedCategory === cat
                          ? "bg-sky-500/15 text-sky-300"
                          : "hover:bg-slate-800/70"
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </aside>

          <main className="flex-1">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/listing/:id" element={<ListingDetail />} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
}
